/***
 *
 *   TICKET CONTEXT BAND
 *   Description and attachments, held above the conversation and OUTSIDE its
 *   scroll container — the two things you keep re-reading while replying, so
 *   they must not scroll away with the feed.
 *
 *   Staying fixed means the band cannot be allowed to grow without bound: the
 *   description is capped and scrolls inside itself only when it is genuinely
 *   long, media becomes a row of small thumbs, and files become chips. The
 *   whole band collapses to its header for people who want the column back.
 *
 *   Renders nothing when there is no description and no attachment.
 *
 **********/

import React, { useEffect, useState } from 'react';
import Style from './context.tailwind';
import TicketAttachment from './media';
import TicketFile from './file';
import { Icon, cn } from 'components/lib';
import { useTranslate } from 'app/translations';

const TicketContextBand = ({ ticket, medias }) => {
    const { _t } = useTranslate();
    const [open, setOpen] = useState(true);

    // A different ticket starts expanded again.
    useEffect(() => {
        setOpen(true);
    }, [ticket.id]);

    const attachmentCount = medias.withImage.length + medias.withFile.length;
    const hasDescription = !!ticket.description?.trim();

    if (!hasDescription && !attachmentCount) return null;

    return (
        <section className={Style.band}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                className={Style.header}>
                <Icon
                    image="chevron-down"
                    size={14}
                    color="currentColor"
                    className={cn(Style.chevron, !open && Style.chevronClosed)}
                />
                <span className={Style.title}>{_t('description')}</span>

                {attachmentCount > 0 && (
                    <span className={Style.count}>
                        <Icon image="paperclip" size={11} color="currentColor" />
                        {attachmentCount}
                    </span>
                )}

                <span className={Style.toggleHint}>
                    {open ? _t('ticket.detail.collapse') : _t('ticket.detail.expand')}
                </span>
            </button>

            {open && (
                <div className={Style.body}>
                    <div className={Style.description}>
                        {hasDescription ? (
                            <div
                                className={Style.descriptionText}
                                dangerouslySetInnerHTML={{
                                    __html: formatDescription(ticket.description),
                                }}
                            />
                        ) : (
                            <p className={Style.descriptionEmpty}>
                                {_t('ticket.detail.noDescription')}
                            </p>
                        )}
                    </div>

                    {attachmentCount > 0 && (
                        <div className={Style.attachments}>
                            <TicketAttachment medias={medias.withImage} compact hideTitle />
                            <TicketFile medias={medias.withFile} compact hideTitle />
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export function formatDescription(data) {
    if (!data) return '';
    let html = data.replace(/(?:\r\n|\r|\n)/g, ' <br /> ');
    html = html.replace(
        /((?:(?:https?|ftp):\/\/|www\.)[^\s]+)/g,
        '<a href="$1" target="_blank" class="text-primary-600" rel="noreferrer">$1</a>',
    );
    html = html.replace(
        /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g,
        '<a href="mailto:$1" class="text-primary-600">$1</a>',
    );
    return html;
}

export default TicketContextBand;
